import { bufferToHex } from './buffer-to-hex.function';

/**
 * Stable SHA-256 of a JSON value. Object keys are sorted so two equal graphs
 * hash the same. Row ids are omitted by the caller: every snapshot recreates
 * its components, so those ids would make every hash unique.
 *
 * Uses the Web Crypto API (available in Node, Workers, and browsers) so this
 * shared helper does not import Node's `crypto` module — Angular apps import
 * ultramarine-shared and cannot resolve that builtin.
 */
export async function hashCanonicalJson(value: unknown): Promise<string> {
  const encoded = new TextEncoder().encode(canonicalize(value));
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return bufferToHex(digest);
}

function canonicalize(value: unknown): string {
  if (value === undefined) {
    return 'null';
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalize(item)).join(',')}]`;
  }

  if (value instanceof Date) {
    return JSON.stringify(value.toISOString());
  }

  if (value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record)
      .filter((key) => record[key] !== undefined)
      .sort();

    return `{${keys
      .map((key) => `${JSON.stringify(key)}:${canonicalize(record[key])}`)
      .join(',')}}`;
  }

  return JSON.stringify(value);
}
